/* ===========================================================
   Custom Shipping + Free Gift Progress Bar
   - Free shipping threshold
   - Auto-add / auto-remove free gift by SKU
   - Listens to theme cart PubSub + falls back to /cart.js
   =========================================================== */
(function () {
  'use strict';

  // Config comes from theme settings via window.__SGB_CONFIG__ (populated by
  // snippets/shipping-gift-bar.liquid). Fallbacks only apply if the snippet
  // wasn't rendered for some reason.
  var FROM_LIQUID = window.__SGB_CONFIG__ || {};
  var CONFIG = {
    enable: FROM_LIQUID.enable !== false,
    giftEnable: FROM_LIQUID.giftEnable !== false,
    freeShippingThreshold: Number(FROM_LIQUID.freeShippingThreshold) || 8900,
    giftThreshold: Number(FROM_LIQUID.giftThreshold) || 12000,
    giftProperty: FROM_LIQUID.giftProperty || '_free_gift',
    giftDiscountCode: FROM_LIQUID.giftDiscountCode || '',
    fsCheckEnable:     !!FROM_LIQUID.fsCheckEnable,
    fsQualifyingTag:   (FROM_LIQUID.fsQualifyingTag || '').toLowerCase(),
    fsExcludedHandle:  FROM_LIQUID.fsExcludedHandle || '',
    fsNoticeText:      FROM_LIQUID.fsNoticeText || '',
    shipText: FROM_LIQUID.shipText || '[amount] left to unlock free shipping!',
    giftText: FROM_LIQUID.giftText || '[amount] left to unlock a free gift!',
    allText:  FROM_LIQUID.allText  || "You've unlocked free shipping + a free gift!",
    currency: FROM_LIQUID.currency ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD'
  };

  // Gift variant id comes exclusively from Theme settings → Shipping Bar →
  // Free gift product (resolved to a variant id by Liquid in the snippet).
  // No other source can supply it — guarantees the selected product is the
  // only one our auto-add flow will ever touch.
  var state = {
    giftVariantId: (FROM_LIQUID.giftVariantId && Number(FROM_LIQUID.giftVariantId)) || null,
    giftProductId: (FROM_LIQUID.giftProductId && Number(FROM_LIQUID.giftProductId)) || null,
    giftLookupTried: false,
    syncing: false,
    lastSyncKey: null,
    firstRenderDone: false,
    hasNonQualifying: false,
    tagCache: {},         // handle -> [tags]
    excludedIds: null     // null = not fetched; {} = fetched (map of product_id -> true)
  };

  /* ---------- utilities ---------- */

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  function escapeText(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // Replace [amount] in a user-configurable template string with a styled span.
  // Template text is escaped; the injected span is trusted HTML we control.
  function substituteAmount(template, cents) {
    var escaped = escapeText(template);
    var span = '<span class="sgb__amount" data-sgb-amount>' + escapeText(formatMoney(cents)) + '</span>';
    return escaped.replace(/\[amount\]/g, span);
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  // A line is "our" auto-gift only when it carries the _free_gift property we
  // stamped at add time. A customer-added copy of the same SKU does not match —
  // we never auto-remove items the customer added themselves.
  function isAutoGift(item) {
    if (!item || !item.properties) return false;
    var v = item.properties[CONFIG.giftProperty];
    return v === 'true' || v === true;
  }

  function findGiftLine(cart) {
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (isAutoGift(cart.items[i])) return cart.items[i];
    }
    return null;
  }

  function getEligibleSubtotal(cart) {
    if (!cart || !cart.items) return 0;
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (isAutoGift(it)) continue;
      total += it.final_line_price;
    }
    return total;
  }

  function syncKeyOf(cart) {
    var eligible = getEligibleSubtotal(cart);
    var gift = findGiftLine(cart);
    return eligible + '|' + (gift ? gift.quantity : 0);
  }

  /* ---------- gift variant resolution ---------- */
  // Primary source: Liquid resolves the variant id from the product picker in
  // Theme Settings → Shipping Bar → Free gift product, and writes it to
  // window.__SGB_CONFIG__.giftVariantId. If that didn't happen (product
  // unpublished, no variants, or theme settings not saved since picker was
  // added), we try a one-shot /products/<handle>.js fetch using the handle
  // Liquid also wrote out.
  function resolveGiftVariantId() {
    if (state.giftVariantId) return Promise.resolve(state.giftVariantId);

    var handle = FROM_LIQUID.giftProductHandle;
    if (!handle) {
      if (window.console && console.warn) {
        console.warn('[shipping-gift-bar] No gift product selected. ' +
          'Open Theme editor → Theme settings → Shipping Bar → Free gift product, pick a product, and save.');
      }
      return Promise.resolve(null);
    }

    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (prod) {
        var variant =
          (prod.variants || []).filter(function (v) { return v.available; })[0] ||
          (prod.variants || [])[0];
        if (variant && variant.id) {
          state.giftVariantId = variant.id;
          return variant.id;
        }
        if (window.console && console.warn) {
          console.warn('[shipping-gift-bar] Gift product "' + (FROM_LIQUID.giftProductTitle || handle) +
            '" has no available variants. Publish it to the Online Store channel and give it a purchasable variant.');
        }
        return null;
      })
      .catch(function () { return null; });
  }

  /* ---------- gift sync (add / remove to match eligibility) ---------- */

  // Returns true when the cart holds a non-auto copy of the gift product —
  // i.e. the customer added the gift product themselves (PDP, quick-add, etc.)
  // without the _free_gift property. Used to gate discount application: a
  // session-level Shopify discount code can't discriminate between lines, so
  // applying it here would also zero the customer's paid line at checkout.
  function hasManualGiftCopy(cart) {
    if (!cart || !cart.items) return false;
    var targetProductId = state.giftProductId;
    var targetVariantId = state.giftVariantId;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (isAutoGift(it)) continue;
      if (targetProductId && it.product_id === targetProductId) return true;
      if (!targetProductId && targetVariantId && it.variant_id === targetVariantId) return true;
    }
    return false;
  }

  // Attach the Shopify discount code to the cart session. redirect:'manual'
  // returns as soon as Set-Cookie headers arrive (~40ms) — no HTML download.
  // When `cart` is provided we use it for the contamination check; otherwise
  // we fetch fresh state. Skips entirely when a manual copy of the gift
  // product is in the cart — see hasManualGiftCopy.
  function applyGiftDiscount(cart) {
    if (!CONFIG.giftDiscountCode) return Promise.resolve();
    var ready = cart ? Promise.resolve(cart) : getCart();
    return ready.then(function (c) {
      if (hasManualGiftCopy(c)) return;
      return fetch('/discount/' + encodeURIComponent(CONFIG.giftDiscountCode), {
        credentials: 'same-origin',
        redirect: 'manual'
      }).then(function () {});
    }).catch(function () {});
  }

  function getCartSectionIds() {
    var ids = [];
    if (document.querySelector('cart-drawer-items') || document.querySelector('cart-drawer')) {
      ids.push('cart-drawer');
    }
    if (document.querySelector('cart-items')) ids.push('main-cart-items');
    ids.push('cart-icon-bubble');
    return ids;
  }

  // Apply Shopify-rendered section HTML returned by /cart/{add,change}.js
  // directly into the DOM — mirrors what the theme would do after a
  // cart-update publish, but skips the extra /cart?section_id=... round-trip.
  function applyCartSections(sectionsMap) {
    if (!sectionsMap) return;

    if (sectionsMap['cart-drawer']) {
      var wrap = document.createElement('div');
      wrap.innerHTML = sectionsMap['cart-drawer'];
      var newItems = wrap.querySelector('cart-drawer-items');
      var oldItems = document.querySelector('cart-drawer-items');
      if (newItems && oldItems) oldItems.replaceWith(newItems);
      var newFooter = wrap.querySelector('.cart-drawer__footer');
      var oldFooter = document.querySelector('.cart-drawer__footer');
      if (newFooter && oldFooter) oldFooter.replaceWith(newFooter);
      var newDrawer = wrap.querySelector('cart-drawer');
      var oldDrawer = document.querySelector('cart-drawer');
      if (newDrawer && oldDrawer) {
        oldDrawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
      }
    }

    if (sectionsMap['main-cart-items']) {
      var wrap2 = document.createElement('div');
      wrap2.innerHTML = sectionsMap['main-cart-items'];
      var newCart = wrap2.querySelector('cart-items');
      var oldCart = document.querySelector('cart-items');
      if (newCart && oldCart) oldCart.innerHTML = newCart.innerHTML;
    }

    if (sectionsMap['cart-icon-bubble']) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) bubble.innerHTML = sectionsMap['cart-icon-bubble'];
    }
  }

  function addGift() {
    return resolveGiftVariantId().then(function (id) {
      if (!id) {
        if (window.console && console.warn) {
          console.warn('[shipping-gift-bar] addGift: no variant id available — gift cannot be added. ' +
            'Check Theme editor → Theme settings → Shipping Bar → Free gift product.');
        }
        return null;
      }
      // Fire discount in parallel — cookie is usually already warmed on init.
      applyGiftDiscount();
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: id,
            quantity: 1,
            properties: (function () { var p = {}; p[CONFIG.giftProperty] = 'true'; return p; })()
          }],
          sections: getCartSectionIds(),
          sections_url: window.location.pathname
        })
      })
        .then(function (r) {
          return r.json().then(function (data) {
            if (!r.ok) {
              if (window.console && console.error) {
                console.error('[shipping-gift-bar] /cart/add.js failed (' + r.status + '):', data);
              }
              return null;
            }
            return data;
          });
        })
        .catch(function (err) {
          if (window.console && console.error) console.error('[shipping-gift-bar] /cart/add.js error:', err);
          return null;
        });
    });
  }

  // Debug helper — paste `window.__sgbAddGiftNow()` in the browser console
  // to force an add and see exactly what happens.
  window.__sgbAddGiftNow = function () {
    return addGift().then(function (resp) {
      console.log('[shipping-gift-bar] addGift response:', resp);
      if (resp && resp.sections) applyCartSections(resp.sections);
      return resp;
    });
  };

  // One-call diagnostic — paste `window.__sgbDebug()` into DevTools console.
  // Prints everything needed to pinpoint why a newly-selected gift product
  // isn't being added.
  window.__sgbDebug = function () {
    var lines = [
      '— SGB debug —',
      'FROM_LIQUID (from theme settings → render-time Liquid):',
      '  bar enabled:         ' + CONFIG.enable,
      '  gift enabled:        ' + CONFIG.giftEnable,
      '  gift threshold (¢):  ' + CONFIG.giftThreshold,
      '  resolved variant id: ' + state.giftVariantId,
      '  product handle:      ' + (FROM_LIQUID.giftProductHandle || '(none)'),
      '  product title:       ' + (FROM_LIQUID.giftProductTitle  || '(none)'),
      '  discount code:       ' + (CONFIG.giftDiscountCode       || '(empty)')
    ];
    console.log(lines.join('\n'));
    return getCart().then(function (cart) {
      var eligible = getEligibleSubtotal(cart);
      var gift = findGiftLine(cart);
      console.log('Cart state:');
      console.log('  total items:         ' + (cart.items ? cart.items.length : 0));
      console.log('  eligible subtotal:   ' + eligible + ' ¢');
      console.log('  threshold met:       ' + (eligible >= CONFIG.giftThreshold));
      console.log('  auto-gift present:   ' + (gift ? ('yes (line ' + gift.key + ', variant ' + gift.variant_id + ')') : 'no'));
      if (gift && state.giftVariantId && gift.variant_id !== state.giftVariantId) {
        console.warn('  ⚠ gift in cart uses an OLD variant id. Cross-variant replace will run on next sync.');
      }
      if (!state.giftVariantId) {
        console.error('  ✗ No resolved variant id. Theme setting → Shipping Bar → Free gift product is not resolving.');
      }
      return cart;
    });
  };

  function changeLine(lineKey, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: quantity,
        sections: getCartSectionIds(),
        sections_url: window.location.pathname
      })
    }).then(function (r) { return r.json(); }).catch(function () { return null; });
  }

  function syncGift(cart) {
    // Gift disabled in theme settings — never add/remove.
    if (!CONFIG.giftEnable) return Promise.resolve(cart);

    // Never run two sync operations concurrently.
    if (state.syncing) return Promise.resolve(cart);

    var key = syncKeyOf(cart);
    // No real price change since last sync — nothing to do. This is the guard
    // that prevents spurious add/remove during DOM refreshes, page loads, or
    // any code path that calls update() without the cart subtotal actually
    // having moved.
    if (state.lastSyncKey === key) return Promise.resolve(cart);

    var eligible = getEligibleSubtotal(cart);
    var giftLine = findGiftLine(cart);
    var qualifies = eligible >= CONFIG.giftThreshold;

    // If the gift in cart no longer matches the currently-configured variant
    // (merchant picked a different product in settings), swap it.
    var wrongVariant = giftLine && state.giftVariantId &&
      giftLine.variant_id !== state.giftVariantId;

    var action = null;
    if (qualifies && !giftLine && !hasManualGiftCopy(cart)) action = 'add';
    else if (!qualifies && giftLine) action = 'remove';
    else if (qualifies && wrongVariant) action = 'replace';
    else if (qualifies && giftLine && giftLine.quantity !== 1) action = 'fix-qty';

    if (!action) {
      state.lastSyncKey = key;
      return Promise.resolve(cart);
    }

    state.syncing = true;
    var op;
    if (action === 'add') op = addGift();
    else if (action === 'remove') op = changeLine(giftLine.key, 0);
    else if (action === 'replace') {
      // Remove the stale gift first, then add the current one.
      op = changeLine(giftLine.key, 0).then(function () { return addGift(); });
    }
    else op = changeLine(giftLine.key, 1);

    return op
      .then(function (response) {
        // Apply sections from the response straight to the DOM — no extra
        // fetch round-trip. This is what makes add/remove feel instant.
        if (response && response.sections) {
          applyCartSections(response.sections);
        }

        state.syncing = false;
        // Force a re-evaluation on the next cart-update so the guard doesn't
        // block a legitimate correction.
        state.lastSyncKey = null;

        // Ask for fresh cart state (cheap — just JSON). Use it to render the
        // shipping bar AND to validate that gift state matches eligibility.
        return getCart().then(function (updated) {
          state.lastSyncKey = syncKeyOf(updated);
          render(updated);

          var eligibleNow = getEligibleSubtotal(updated);
          var giftNow = findGiftLine(updated);
          var qualifiesNow = eligibleNow >= CONFIG.giftThreshold;
          var drift =
            (qualifiesNow && !giftNow) ||
            (!qualifiesNow && giftNow) ||
            (qualifiesNow && giftNow && giftNow.quantity !== 1);
          if (drift) {
            state.lastSyncKey = null;
            update();
          }
          return updated;
        });
      })
      .catch(function () {
        state.syncing = false;
        return cart;
      });
  }

  // The theme declares `subscribe` / `publish` as plain top-level functions in
  // assets/pubsub.js and `PUB_SUB_EVENTS` as a top-level `const` in
  // assets/constants.js. `const` at script-top-level is NOT exposed on window,
  // so we must look these up via bare-name script-scope resolution (which
  // works inside this IIFE).
  function getPubSubEvents() {
    try { return (typeof PUB_SUB_EVENTS !== 'undefined') ? PUB_SUB_EVENTS : null; }
    catch (e) { return null; }
  }
  function getSubscribe() {
    try { return (typeof subscribe === 'function') ? subscribe : null; }
    catch (e) { return null; }
  }
  function getPublish() {
    try { return (typeof publish === 'function') ? publish : null; }
    catch (e) { return null; }
  }

  // Let the theme handle DOM re-rendering (surgical, no flicker).
  function notifyCartUpdate() {
    var pub = getPublish();
    var events = getPubSubEvents();
    if (pub && events && events.cartUpdate) {
      pub(events.cartUpdate, { source: 'sgb-gift' });
    }
  }

  /* ---------- DOM re-render is owned by the theme's own cart pipeline ---------- */

  /* ---------- render ---------- */

  function render(cart) {
    var bars = document.querySelectorAll('[data-shipping-gift-bar]');
    if (!bars.length) return;

    // Hide when cart is empty — backstop for the case where the bar's surgical
    // section update leaves a stale bar element in the DOM after the last item
    // (or auto-gift) has been removed. Initial page load is gated by Liquid.
    var isEmpty = !cart || !cart.item_count;
    Array.prototype.forEach.call(bars, function (bar) {
      bar.style.display = isEmpty ? 'none' : '';
    });
    if (isEmpty) return;

    var eligible = getEligibleSubtotal(cart);
    var shipT = CONFIG.freeShippingThreshold;
    var giftT = CONFIG.giftThreshold;
    var remainingShip = Math.max(shipT - eligible, 0);
    var remainingGift = Math.max(giftT - eligible, 0);

    // When the gift is disabled, the bar scale ends at the shipping threshold.
    var giftPos, shipPos, fillPct;
    if (!CONFIG.giftEnable) {
      giftPos = 100;
      shipPos = 100;
      fillPct = Math.min((eligible / shipT) * 100, 100);
      if (eligible >= shipT) fillPct = 100;
    } else {
      giftPos = 90;
      shipPos = (shipT / giftT) * giftPos;
      fillPct = Math.min((eligible / giftT) * giftPos, 100);
      if (eligible >= giftT) fillPct = 100;
    }

    // Build the message from configurable templates with [amount] substitution.
    // If the cart has any non-qualifying item (tag / excluded collection check),
    // override with the settings-driven notice text — the "$X left" message
    // would be misleading since no amount added will make the cart qualify.
    var messageHtml;
    if (CONFIG.fsCheckEnable && state.hasNonQualifying && cart && cart.item_count > 0) {
      messageHtml = escapeText(CONFIG.fsNoticeText);
    } else if (CONFIG.giftEnable && eligible >= giftT) {
      messageHtml = escapeText(CONFIG.allText);
    } else if (CONFIG.giftEnable && eligible >= shipT) {
      messageHtml = substituteAmount(CONFIG.giftText, remainingGift);
    } else if (!CONFIG.giftEnable && eligible >= shipT) {
      messageHtml = escapeText(CONFIG.allText);
    } else {
      var show = remainingShip > 0 ? remainingShip : shipT;
      messageHtml = substituteAmount(CONFIG.shipText, show);
    }

    Array.prototype.forEach.call(bars, function (bar) {
      var fill = bar.querySelector('[data-sgb-fill]');
      var ball = bar.querySelector('[data-sgb-ball]');
      var amountEl = bar.querySelector('[data-sgb-amount]');
      var textEl = bar.querySelector('[data-sgb-text]');
      var shipM = bar.querySelector('[data-sgb-milestone="ship"]');
      var giftM = bar.querySelector('[data-sgb-milestone="gift"]');
      var shipPrice = bar.querySelector('[data-sgb-price-ship]');
      var giftPrice = bar.querySelector('[data-sgb-price-gift]');
      var shipTick = bar.querySelector('[data-sgb-tick-ship]');
      var giftTick = bar.querySelector('[data-sgb-tick-gift]');

      // Each bar element (drawer or page) jumps to its current position
      // without animation the very first time we render into it. After that,
      // width/left changes transition smoothly forward from the current value.
      var fresh = !bar.dataset.sgbReady;
      if (fresh) bar.classList.add('sgb--no-transition');

      if (fill) fill.style.width = fillPct + '%';
      if (ball) ball.style.left = fillPct + '%';

      if (fresh) {
        void bar.offsetWidth;
        bar.classList.remove('sgb--no-transition');
        bar.dataset.sgbReady = '1';
      }

      if (shipM) shipM.style.setProperty('--sgb-pos', shipPos + '%');
      if (giftM) giftM.style.setProperty('--sgb-pos', giftPos + '%');
      if (shipTick) shipTick.style.setProperty('--sgb-pos', shipPos + '%');
      if (giftTick) giftTick.style.setProperty('--sgb-pos', giftPos + '%');

      if (shipPrice) shipPrice.textContent = formatMoney(shipT);
      if (giftPrice) giftPrice.textContent = formatMoney(giftT);

      // Replace full message HTML (contains the red amount span inline).
      var msg = bar.querySelector('[data-sgb-message]');
      if (msg) msg.innerHTML = messageHtml;

      if (shipM) shipM.classList.toggle('is-reached', eligible >= shipT);
      if (giftM) giftM.classList.toggle('is-reached', eligible >= giftT);
      bar.classList.toggle('is-shipping-reached', eligible >= shipT);
      bar.classList.toggle('is-gift-reached', eligible >= giftT);
      bar.classList.toggle('is-all-reached', eligible >= giftT);
    });

    markGiftLines(cart);
    overrideTotals(cart);
  }

  // Safety: if the gift variant ever ends up in cart at a non-zero price
  // (e.g. misconfigured variant price), subtract it from the displayed total.
  // With the variant priced at $0 in admin, this is a no-op.
  function overrideTotals(cart) {
    if (!cart) return;
    var gift = findGiftLine(cart);
    var giftPrice = gift ? gift.final_line_price : 0;
    if (!giftPrice) return;

    var adjusted = Math.max(cart.total_price - giftPrice, 0);

    var totalEls = document.querySelectorAll(
      '.totals__total-value, .cart-footer__total-value, .totals__subtotal-value'
    );
    Array.prototype.forEach.call(totalEls, function (el) {
      var currency = /\b([A-Z]{3})\b/.exec(el.textContent);
      el.textContent = formatMoney(adjusted) + (currency ? ' ' + currency[1] : '');
    });
  }

  function markGiftLines(cart) {
    if (!cart || !cart.items) return;
    // Clear stale markers first.
    var stale = document.querySelectorAll('.cart-item.sgb-gift-line');
    for (var s = 0; s < stale.length; s++) stale[s].classList.remove('sgb-gift-line');

    // STRICT: only the line that carries the _free_gift property we stamped
    // at add-time. A customer-purchased copy of the same product is never
    // styled as free.
    var idx = -1;
    for (var i = 0; i < cart.items.length; i++) {
      if (isAutoGift(cart.items[i])) { idx = i; break; }
    }
    if (idx < 0) return;
    var lineNum = idx + 1;

    var rows = [];
    var drawerRow = document.getElementById('CartDrawer-Item-' + lineNum);
    if (drawerRow) rows.push(drawerRow);
    var cartRow = document.getElementById('CartItem-' + lineNum);
    if (cartRow) rows.push(cartRow);

    rows.forEach(function (row) {
      row.classList.add('sgb-gift-line');
    });
  }

  /* ---------- free-shipping eligibility (tag + excluded-collection) ---------- */
  function fetchProductTags(handle) {
    if (!handle) return Promise.resolve([]);
    if (state.tagCache[handle]) return Promise.resolve(state.tagCache[handle]);
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (prod) {
        var tags = (prod && prod.tags) ? prod.tags.map(function (t) { return String(t).toLowerCase(); }) : [];
        state.tagCache[handle] = tags;
        return tags;
      })
      .catch(function () { return []; });
  }

  function fetchExcludedIds() {
    var handle = CONFIG.fsExcludedHandle;
    if (!handle) { state.excludedIds = state.excludedIds || {}; return Promise.resolve(state.excludedIds); }
    if (state.excludedIds) return Promise.resolve(state.excludedIds);
    return fetch('/collections/' + handle + '/products.json?limit=250', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var set = {};
        (data && data.products || []).forEach(function (p) { set[p.id] = true; });
        state.excludedIds = set;
        return set;
      })
      .catch(function () { state.excludedIds = {}; return state.excludedIds; });
  }

  function evaluateEligibility(cart) {
    if (!CONFIG.fsCheckEnable) { state.hasNonQualifying = false; return Promise.resolve(false); }
    if (!cart || !cart.items || !cart.items.length) { state.hasNonQualifying = false; return Promise.resolve(false); }
    return fetchExcludedIds().then(function (excluded) {
      var checks = cart.items.map(function (item) {
        if (isAutoGift(item)) return Promise.resolve(true); // auto-gift line doesn't block eligibility
        return fetchProductTags(item.handle).then(function (tags) {
          var tagOk = tags.indexOf(CONFIG.fsQualifyingTag) !== -1;
          var inExcluded = !!excluded[item.product_id];
          return tagOk && !inExcluded;
        });
      });
      return Promise.all(checks).then(function (rows) {
        var hasNonQ = rows.indexOf(false) !== -1;
        state.hasNonQualifying = hasNonQ;
        return hasNonQ;
      });
    });
  }

  /* ---------- main update cycle ---------- */

  function update(maybeCart) {
    var p = (maybeCart && maybeCart.items) ? Promise.resolve(maybeCart) : getCart();
    return p.then(function (cart) {
      return syncGift(cart).then(function (finalCart) {
        var settled = finalCart || cart;
        // Resolve eligibility before rendering so the message reflects it.
        return evaluateEligibility(settled).then(function () {
          render(settled);
          return settled;
        });
      });
    }).catch(function () {});
  }

  /* ---------- event wiring ---------- */

  function wire() {
    // Retry until pubsub.js + constants.js are parsed and the bindings exist.
    (function subscribeWhenReady(attempts) {
      var sub = getSubscribe();
      var events = getPubSubEvents();
      if (sub && events && events.cartUpdate) {
        sub(events.cartUpdate, function (event) {
          // Theme fires cartUpdate with { source, cartData } — use that cart
          // state directly when present to skip an extra /cart.js round-trip.
          var data = event && event.cartData;
          update(data && data.items ? data : undefined);
        });
        return;
      }
      if (attempts >= 40) return; // ~4s timeout — give up silently.
      setTimeout(function () { subscribeWhenReady(attempts + 1); }, 100);
    })(0);
    // Legacy / generic cart refresh hooks
    document.addEventListener('cart:updated', function () { update(); });
    document.addEventListener('cart:refresh', function () { update(); });
    document.addEventListener('cart:added', function () { update(); });

    // Catch any /cart/* mutation and fire update() so syncGift re-evaluates.
    var _fetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = (init && init.method) || (input && input.method) || 'GET';
      var res = _fetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)(\.js|\.json)?/i.test(url) && method.toUpperCase() !== 'GET') {
        res.then(function () { update(); }).catch(function () {});
      }
      return res;
    };

    // Theme replaces cart items / footer async after cart-update. Re-apply
    // our gift line markings + totals override once that DOM settles.
    var debounceTimer = null;
    var bodyObs = new MutationObserver(function (mutations) {
      var relevant = false;
      for (var i = 0; i < mutations.length && !relevant; i++) {
        var m = mutations[i];
        if (m.type !== 'childList') continue;
        var t = m.target;
        if (!t) continue;
        var tag = t.tagName;
        // Only react to structural swaps of the cart item containers.
        if (tag === 'CART-DRAWER-ITEMS' || tag === 'CART-ITEMS') { relevant = true; break; }
        var cls = t.classList;
        if (cls && (cls.contains('js-contents') || cls.contains('cart-drawer__footer'))) {
          relevant = true;
        }
      }
      if (!relevant) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        getCart().then(function (cart) {
          markGiftLines(cart);
          overrideTotals(cart);
          render(cart);
        }).catch(function () {});
      }, 0);
    });
    bodyObs.observe(document.body, { childList: true, subtree: true });
  }

  // If the gift is in cart but its line price wasn't zeroed, re-apply the
  // discount code so checkout charges $0.
  function ensureGiftDiscountApplied() {
    if (!CONFIG.giftEnable || !CONFIG.giftDiscountCode) return;
    getCart().then(function (cart) {
      var gift = findGiftLine(cart);
      if (gift && gift.final_line_price > 0) applyGiftDiscount(cart);
    }).catch(function () {});
  }

  // Intercept Checkout clicks so the discount cookie is guaranteed fresh
  // in the session the checkout page inherits.
  // function interceptCheckout() {
  //   if (!CONFIG.giftEnable || !CONFIG.giftDiscountCode) return;
  //   var blocking = false;
  //   document.addEventListener('click', function (e) {
  //     if (blocking) return;
  //     var btn = e.target && e.target.closest && e.target.closest(
  //       'button[name="checkout"], input[name="checkout"], a[href*="/checkout"]'
  //     );
  //     if (!btn) return;
  //     var form = btn.tagName === 'A' ? null : btn.closest('form');
  //     var href = btn.tagName === 'A' ? btn.href : null;
  //     e.preventDefault(); e.stopPropagation();
  //     blocking = true;
  //     getCart().then(function (cart) {
  //       var gift = findGiftLine(cart);
  //       if (!gift || gift.final_line_price === 0) return Promise.resolve();
  //       return applyGiftDiscount(cart);
  //     }).catch(function () {}).then(function () {
  //       blocking = false;
  //       if (form && typeof form.submit === 'function') form.submit();
  //       else if (href) window.location.href = href;
  //       else btn.click();
  //     });
  //   }, true);
  // }

   function interceptCheckout() {
    if (!CONFIG.giftEnable || !CONFIG.giftDiscountCode) return;
    var blocking = false;
    document.addEventListener('click', function (e) {
      if (blocking) return;
      var btn = e.target && e.target.closest && e.target.closest(
        'button[name="checkout"], input[name="checkout"], a[href*="/checkout"]'
      );
      if (!btn) return;
      // btn.form follows the HTMLButtonElement form="…" association, so this
      // resolves correctly for the drawer/cart-page buttons that live OUTSIDE
      // their form (closest('form') would return null for those). closest is
      // the fallback for buttons inside their form (e.g. cart-notification).
      var form = btn.tagName === 'A' ? null : (btn.form || btn.closest('form'));
      var href = btn.tagName === 'A' ? btn.href : null;
      e.preventDefault(); e.stopPropagation();
      blocking = true;
      getCart().then(function (cart) {
        var gift = findGiftLine(cart);
        if (!gift || gift.final_line_price === 0) return Promise.resolve();
        return applyGiftDiscount(cart);
      }).catch(function () {}).then(function () {
        blocking = false;
        if (href) {
          window.location.href = href;
        } else if (form && typeof form.requestSubmit === 'function') {
          // requestSubmit includes the submitter's name="checkout" in the
          // POST body — the field Shopify's /cart endpoint looks for to
          // redirect onward to /checkout. form.submit() drops the submitter.
          form.requestSubmit(btn);
        } else if (form && typeof form.submit === 'function') {
          form.submit();
        }
      });
    }, true);
  }

  function init() {
    if (!document.querySelector('[data-shipping-gift-bar]')) return;
    wire();
    interceptCheckout();
    resolveGiftVariantId().finally(function () {
      update();
      // Only apply the discount when our auto-gift (an .sgb-gift-line row
      // with properties._free_gift === 'true') is actually in the cart.
      ensureGiftDiscountApplied();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
